"use client";

import React from "react";
import { Activity, CheckCircle2, AlertCircle, User, Home, Heart, ClipboardCheck, Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function PuppyAvailabilityForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-12 text-center bg-background border border-border shadow-sm animate-in fade-in duration-700">
        <CheckCircle2 className="size-16 text-secondary mb-6" />
        <h3 className="text-3xl font-serif text-foreground mb-4">Application Received</h3>
        <p className="text-foreground/70 mb-8 max-w-md font-sans text-lg">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground font-sans uppercase tracking-widest text-xs px-8 py-6">
          Submit Another Application
        </Button>
      </div>;
  }
  const sectionHeadingClass = "text-xl font-serif text-foreground mb-6 flex items-center gap-3 border-b border-border pb-2 mt-10 first:mt-0";
  const labelClass = "text-foreground/70 font-sans text-[12px] uppercase tracking-[0.1em] font-bold";
  return <Form onSubmit={handleSubmit} className="space-y-0">
      <div className="space-y-12">
        {}
        <section>
          <h3 className={sectionHeadingClass}>
            <User className="size-5 text-primary" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <FormField control={control} name="applicant_name" rules={{
            required: "Your full name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Your Full Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Jane Doe" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">First and last name as it appears on legal documents.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "Email is required",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email address"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Email Address</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="jane@example.com" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">We'll use this to contact you about your application.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[0-9\-+() ]+$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Phone Number</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" placeholder="(555) 123‑1234" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">Best number to reach you during business hours.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <div className="md:col-span-2">
              <FormField control={control} name="address" render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>Mailing Address</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="123 Main St, City, State, ZIP" className="rounded-none min-h-[80px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">Where you would like the puppy delivered or picked up.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </section>

        {}
        <section>
          <h3 className={sectionHeadingClass}>
            <Heart className="size-5 text-primary" />
            Puppy Preferences
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <FormField control={control} name="preferred_sex" render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Preferred Puppy Sex</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="rounded-none h-12 border-border focus:ring-primary">
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-none">
                      <SelectItem value="No Preference">No Preference</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-[11px]">Select if you have a specific preference.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="expected_placement_date" render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Preferred Placement Date</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">When would you like to take your puppy home?</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <div className="md:col-span-2">
              <FormField control={control} name="reason_for_puppy" rules={{
              required: "Please tell us why you want a Cavalier"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>Why do you want a Cavalier King Charles Spaniel?</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="rounded-none min-h-[100px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">Explain your motivation and what you hope the puppy will bring to your family.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </section>

        {}
        <section>
          <h3 className={sectionHeadingClass}>
            <Home className="size-5 text-primary" />
            Housing & Environment
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <FormField control={control} name="housing_type" rules={{
            required: "Housing type is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Housing Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="rounded-none h-12 border-border focus:ring-primary">
                        <SelectValue placeholder="Select housing type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Apartment (no yard)">Apartment (no yard)</SelectItem>
                      <SelectItem value="Apartment (with yard)">Apartment (with yard)</SelectItem>
                      <SelectItem value="House with yard">House with yard</SelectItem>
                      <SelectItem value="Farm/Ranch">Farm/Ranch</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-[11px]">Choose the type of home you live in.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="yard_fenced" rules={{
            required: "Please indicate if your yard is fenced"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Is your yard fenced?</FormLabel>
                  <div className="flex gap-6 pt-2">
                    {["Yes", "No", "Not applicable"].map((option, i) => <label key={option} data-index={i} className="flex items-center gap-2 cursor-pointer group">
                        <input type="radio" name="yard_fenced" value={option} checked={field.value === option} onChange={() => field.onChange(option)} className="size-4 border-border text-primary focus:ring-primary accent-primary" />
                        <span className="text-sm font-sans group-hover:text-primary transition-colors">{option}</span>
                      </label>)}
                  </div>
                  <FormDescription className="text-[11px]">If you have a yard, indicate whether it is securely fenced.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="hours_at_home" rules={{
            required: "Typical hours at home is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Typical Hours Spent at Home</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="rounded-none h-12 border-border focus:ring-primary">
                        <SelectValue placeholder="Select hours" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Less than 4 hours">Less than 4 hours</SelectItem>
                      <SelectItem value="4–8 hours">4–8 hours</SelectItem>
                      <SelectItem value="8–12 hours">8–12 hours</SelectItem>
                      <SelectItem value="More than 12 hours">More than 12 hours</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-[11px]">Select the range that best describes your daily presence at home.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <div className="md:col-span-2">
              <FormField control={control} name="current_pets" rules={{
              required: "Please list your current pets"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>Current Pets</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="e.g., 1 adult Labrador Retriever, neutered, 5 years old" className="rounded-none min-h-[80px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">List all current animals in your household (species, breed, age, sex, spayed/neutered status).</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </section>

        {}
        <section>
          <h3 className={sectionHeadingClass}>
            <ClipboardCheck className="size-5 text-primary" />
            Veterinary & Experience
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <FormField control={control} name="vet_name" rules={{
            required: "Veterinarian name is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Veterinarian Name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Dr. Smith" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">Name of your primary vet (if any).</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="vet_contact" rules={{
            required: "Veterinarian phone is required",
            pattern: {
              value: /^[0-9\-+() ]+$/,
              message: "Invalid phone number"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>Veterinarian Phone</FormLabel>
                  <FormControl>
                    <Input {...field} type="tel" placeholder="(555) 987‑6543" className="rounded-none h-12 border-border focus-visible:ring-primary" />
                  </FormControl>
                  <FormDescription className="text-[11px]">Phone number for your veterinarian.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <div className="md:col-span-2">
              <FormField control={control} name="experience" rules={{
              required: "Please describe your experience with dogs"
            }} render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>Experience with Dogs</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Describe your experience caring for dogs, especially small breeds..." className="rounded-none min-h-[100px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">Helps us ensure a good match for the puppy.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="md:col-span-2">
              <FormField control={control} name="references" render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>References (Optional)</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder="Names and contact info for 2 references who can speak to your pet ownership experience." className="rounded-none min-h-[80px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">Provide references if available.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>
          </div>
        </section>

        {}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <FormField control={control} name="how_heard" rules={{
            required: "Please let us know how you heard about us"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className={labelClass}>How did you hear about us?</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger className="rounded-none h-12 border-border focus:ring-primary">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="rounded-none">
                      <SelectItem value="Google Search">Google Search</SelectItem>
                      <SelectItem value="Facebook">Facebook</SelectItem>
                      <SelectItem value="Instagram">Instagram</SelectItem>
                      <SelectItem value="Word of Mouth">Word of Mouth</SelectItem>
                      <SelectItem value="Veterinarian">Veterinarian</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <div className="md:col-span-2">
              <FormField control={control} name="additional_comments" render={({
              field
            }) => <FormItem>
                    <FormLabel className={labelClass}>Additional Comments or Questions</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="rounded-none min-h-[80px] border-border focus-visible:ring-primary" />
                    </FormControl>
                    <FormDescription className="text-[11px]">Any other information you'd like to share.</FormDescription>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <div className="md:col-span-2 pt-4">
              <FormField control={control} name="agree_terms" rules={{
              required: "You must agree to be contacted to submit your application"
            }} render={({
              field
            }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-none border border-primary/20 p-4 bg-primary/5">
                    <FormControl>
                      <input type="checkbox" checked={field.value} onChange={field.onChange} className="size-5 mt-1 rounded-none border-primary text-primary focus:ring-primary accent-primary cursor-pointer" />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-sans font-medium cursor-pointer">
                        I agree to be contacted via email and or phone by Mile High Cavaliers
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>} />
            </div>
          </div>
        </section>

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-none flex items-start gap-3">
            <AlertCircle className="size-5 text-destructive shrink-0 mt-0.5" />
            <p className="text-destructive text-sm font-sans">{submitError}</p>
          </div>}

        <div className="flex justify-center pt-6">
          <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[300px] rounded-none bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-8 text-sm font-sans font-bold tracking-[0.2em] uppercase transition-all shadow-lg hover:shadow-xl">
            {isSubmitting ? <>
                <Loader2 className="animate-spin mr-2 size-5" />
                Processing Application...
              </> : "Submit Application"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function PuppyAvailability() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="puppy-application" className="w-full bg-background py-24 md:py-32 overflow-hidden text-foreground" ref={ref}>
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="flex flex-col items-center text-center mb-16">
          <Badge className="mb-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground border-none px-6 py-2.5 text-[10px] font-sans font-bold uppercase tracking-[0.2em] shadow-sm" variant="default">HIGH Breeding Standards</Badge>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tight mb-6">
            Puppy Application Form
          </h2>
          <p className="text-foreground/70 max-w-2xl mx-auto text-lg md:text-xl font-sans leading-relaxed">Thank you for your interest in adopting one of our Cavalier King Charles puppies! While we do not currently have any available puppies, we can refer you to other breeders that follow  our similar breeding protocols. Please fill out this application to help us find the best forever home for our dogs.</p>
        </div>

        <div className="bg-card p-6 md:p-12 rounded-none border-none shadow-[0_10px_40px_-15px_rgba(0,0,0,0.05)]">
          <FormProvider formKey="puppy_application_form_aboutcavaliers" formId="puppy_application_form_aboutcavaliers" sectionName="puppy-availability" defaultValues={{
          applicant_name: "",
          email: "",
          phone: "",
          address: "",
          preferred_sex: "No Preference",
          experience: "",
          references: "",
          agree_terms: false,
          current_pets: "",
          housing_type: "",
          yard_fenced: "",
          hours_at_home: "",
          vet_name: "",
          vet_contact: "",
          reason_for_puppy: "",
          expected_placement_date: "",
          how_heard: "",
          additional_comments: ""
        }} submitText="Submit Application" successMessage="Thank you for your application! We have received your details and our team will review your information to ensure a perfect match for our puppies.">
            <PuppyAvailabilityForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}